import * as React from 'react';
import { evolve, isEmpty, mergeRight, test, values } from 'ramda';
import { FormattedMessage } from 'react-intl';
import { styled } from '@mui/system';
import { connect } from 'react-redux';

import { Item, Container } from '../../../components/Grid/index';
import InputHandler from '../../../components/InputHandler';
import colors from '../../../config/theme/colors';

import { getJWTData } from '../../../utility/jwtAuthTools';
import { exists, parsePatientDemographics } from 'neuro-utils';

import CreationForm from './CreationForm';
import { actions } from '../../../store/session';
import { ActionableErrorContext, Loggable, makeLog } from '../../../utility/logger';
import { TDispatch, ws_manager, type RootState } from '../../../store';
import { createPatient } from 'Store/patient/apiFetchers';
import { IPatientDataForm } from 'Store/patient/util';
import ExtendedSearch, { IExtendedSearch } from './ExtendedSearch';
import ActionButtonRounded from 'Components/ActionButtonRounded';
import NewSearchResults from '../components/SearchResults';
import SearchSpinner from '../components/SearchSpinner';
import { actions as searchActions } from 'Store/patientsearch';
import { clearAllPatientDataAndSettings } from 'Store/combinedActions';
import { jwtModify, patientSearch, patientSelect } from '../fetchers';
import { StyledHeader } from '../components';
import { defaultLogicalOperators, defaultSearchValues } from './ExtendedSearch/config';
import { makeFinalSearchStringArray } from './ExtendedSearch/functions';

const StyledSearchArea = styled('div')({});
const StyledSearchInfo = styled('div')({});

// Validate some part of the creation data, such as names and ssn
const validateForm = (newForm: IOwnState['newForm']): { [key: string]: boolean | string | null } => {
  const format = {
    firstNames: test(/([\w]+)/),
    lastNames: test(/([\w]+)/),
  };
  return evolve(format, newForm);
};

/** Delay in milliseconds before showing search results. */
const searchResultsDelay = 200;

type Props = IOwnProps & IStateFromProps & IDispatchFromProps;

const newFormDefaultData = {
  firstNames: '',
  lastNames: '',
  ssn: '',
  ssnLocalization: 'FIN', // Use fi localization format for now
  address: null,
  addressDetail: null,
  city: null,
  zipCode: null,
  country: null,
};

class PatientSearchAndCreation extends React.Component<Props, IOwnState> {
  mounted: boolean;
  timeout: NodeJS.Timeout | null;
  constructor(props: Props) {
    super(props);
    this.mounted = true;
    this.timeout = null;
    this.state = {
      searchLoading: false,
      searchText: '',
      wildcard: 'both',
      selected: '',
      newForm: {
        ...newFormDefaultData,
        firstNames: !this.props.session.data?.patientid ? this.props.session.data?.patientfirstnames || '' : '',
        lastNames: !this.props.session.data?.patientid ? this.props.session.data?.patientlastnames || '' : '',
        ssn: !this.props.session.data?.patientid ? this.props.session.data?.patientssn || '' : '',
      },
      validated: {},
      goToVisitReason: false,
      org: getJWTData()?.orgid || '',
      ssnValid: true,
      ssnInvalidMessage: 'general.checkSsn',
      ssnBRValidationLoading: false,
      platform: undefined,
      searchFields: defaultSearchValues,
      logicalOperators: defaultLogicalOperators,
    };
  }

  componentWillUnmount = (): void => {
    this.mounted = false;
  };

  public resetState = (): void => {
    this.setState({
      selected: '',
      newForm: newFormDefaultData,
      validated: {},
      goToVisitReason: false,
      ssnValid: true,
    });
  };

  public checkSsn = (ssn: string, ssnLocalization: 'FIN' | 'SWE', onlyClear?: boolean): void => {
    this.timeout && clearTimeout(this.timeout);
    if (onlyClear) {
      this.setState({
        ssnBRValidationLoading: false,
      });
      return;
    }

    this.setState({ ssnBRValidationLoading: true });
    this.timeout = setTimeout(() => {
      // Check if ssn exists -> if it does, display a popper saying that the ssn must be checked
      void patientSearch(this.state.org, ssn).then(patients => {
        const numberOfPatients = patients?.filter(p => p.ssnLocalization === ssnLocalization)?.length || 0;
        if (numberOfPatients > 0) {
          this.setState({
            ssnInvalidMessage: 'general.ssnDuplicate',
            ssnValid: false,
            ssnBRValidationLoading: false,
          });
        } else {
          this.setState({
            ssnValid: true,
            ssnBRValidationLoading: false,
          });
        }
      });
    }, 1000);
  };

  public onSelectPatient = (id: string) => (): void => {
    this.setState({ selected: id });
  };

  public onChangeSearchText = (values: TOnChangeValues): void => {
    const name = Object.keys(values)[0];
    const value = values[name] as string;

    if ((value.trim().match(/ /g) || []).length > 1) {
      this.setState({ validated: { ...this.state.validated, searchText: false } });
    } else {
      this.setState({ validated: { ...this.state.validated, searchText: true } });
    }
    this.setState({ searchText: value });
  };

  public onChangeNewForm = (values: TOnChangeValues): void => {
    const name = Object.keys(values)[0] as keyof IOwnState['newForm'];
    const value = values[name] as string;

    // Clear the backend ssn check timeout every time any relevant values change
    if (name === 'ssn' || name === 'ssnLocalization') {
      this.checkSsn('', 'FIN', true);
    }

    if (name === 'ssn') {
      const patientCtr = this.state.newForm.ssnLocalization as 'FIN' | 'SWE';
      const patientDemographics = parsePatientDemographics(patientCtr, { ssn: value });
      if (!patientDemographics) {
        this.setState({
          ssnInvalidMessage: 'general.checkSsn',
          ssnValid: false,
        });
      } else {
        this.setState({
          ssnValid: true,
        });
        this.checkSsn(value, patientCtr);
      }
    }
    if (name === 'ssnLocalization' && value) {
      const patientSSN = this.state.newForm.ssn;
      const patientDemographics = parsePatientDemographics(value as 'FIN' | 'SWE', { ssn: patientSSN });
      if (!patientDemographics) {
        this.setState({
          ssnInvalidMessage: 'general.checkSsn',
          ssnValid: false,
        });
      } else {
        this.setState({
          ssnValid: true,
        });
        this.checkSsn(patientSSN, value as 'FIN' | 'SWE');
      }
    }

    if (name === 'firstNames' || name === 'lastNames') {
      // Empty stays neutral (null), typed-but-rejected flips to false
      const nameValidity = value.trim().length === 0 ? null : test(/([\w]+)/, value);
      this.setState({ validated: { ...this.state.validated, [name]: nameValidity } });
    }

    this.setState({ newForm: mergeRight(this.state.newForm, { [name]: value }) });
  };

  // onChange function to pass as props to the ExtendedSearch component
  public onChangePlatform = (values: TOnChangeValues): void => {
    const name = Object.keys(values)[0];
    const value = values[name];

    this.props.clearSearchResults();
    this.setState({ platform: value as Platform | undefined, searchFields: defaultSearchValues });
  };

  // onChange function to pass as props to the ExtendedSearch component
  public onChangeExtendedSearchField = (values: TOnChangeValues): void => {
    const newValues = Object.fromEntries(
      Object.entries(values).map(([key, value]) => [key, !exists(value) || isEmpty(value) ? 'noCropping' : value]),
    );

    this.setState({
      searchFields: {
        ...this.state.searchFields,
        ...newValues,
      },
    });
  };

  // onChange function to pass as props to the ExtendedSearch component
  public onChangeLogicalOperator = (values: TOnChangeValues): void => {
    const name = Object.keys(values)[0];
    const value = values[name];

    const setLogicalOperators = (field: string, value: 'AND' | 'OR') => {
      this.setState({ logicalOperators: { ...this.state.logicalOperators, [field]: value } });
    };

    if (value === 'AND' || value === 'OR') {
      setLogicalOperators(name, value);
    }
  };

  public search = (): void => {
    if (this.state.searchText.trim().length > 0) {
      this.setState({ searchLoading: true });

      void this.props.doPatientSearch(this.state.searchText.trim(), this.state.wildcard).then(() => {
        // Artificial timeout to let spinner spin
        setTimeout(() => {
          this.setState({ searchLoading: false });
        }, searchResultsDelay);
      });
    }
  };

  public extendedSearch = (): void => {
    // To indicate to user that the search is executed, briefly hide results i.e. "flash" the area
    this.setState({ searchLoading: true });

    const searchQueryStrings = makeFinalSearchStringArray(
      this.state.platform,
      this.state.searchFields,
      this.state.logicalOperators,
    );

    if (!(searchQueryStrings.length === 1 && searchQueryStrings[0] === 'search=')) {
      void this.props.doPatientExtendedSearch(searchQueryStrings.join('&')).then(() => {
        // Artificial timeout to let spinner spin
        setTimeout(() => {
          this.setState({ searchLoading: false });
        }, searchResultsDelay);
      });
    }
  };

  public submitNewPatient = (): void => {
    if (values(validateForm(this.state.newForm)).includes(false)) {
      // Validation fail
      this.setState({ validated: validateForm(this.state.newForm) });
    } else {
      this.setState({ validated: validateForm(this.state.newForm) });
      createPatient(this.state.newForm)
        .then((res: Response | null) => {
          if (res) {
            this.setState({ newForm: newFormDefaultData }); // Empty form
            this.fetchNewJWT(res);
          }
        })
        .catch((err: Error) =>
          makeLog(Loggable.BugActionable, err, new ActionableErrorContext(null, 'create patient')),
        );
    }
  };

  public moveToVisitFromSearch = (id?: string): void => {
    const patientid = this.state.selected || id;
    if (patientid) {
      void patientSelect(patientid).then(res => res && this.fetchNewJWT(res));
    }
  };

  public fetchNewJWT = (res: Response): void => {
    if (res.status === 200) {
      void res.text().then((text: string) => {
        void jwtModify(text).then(success => {
          if (success) {
            this.props.loadSession();
            this.props.clearAllPatientData();
          }
        });
      });
    }
  };

  private handleEnter = (e: React.KeyboardEvent<HTMLDivElement>): void => {
    if (e.key === 'Enter' && this.state.validated?.searchText !== false) {
      this.search();
    }
  };

  public render(): React.JSX.Element {
    const { searchResults } = this.props;
    const { searchText, newForm, validated } = this.state;
    const { patientid, patientfirstnames, patientlastnames, patientssn } = this.props.session.data || {};

    /** State relevant to ExtendedSearch component. */
    const extendedSearchState = {
      platform: this.state.platform,
      searchFields: this.state.searchFields,
      searchLoading: this.state.searchLoading,
      logicalOperators: this.state.logicalOperators,
    };

    const namesPresent = newForm.firstNames.trim().length > 0 && newForm.lastNames.trim().length > 0;
    const namesNotInvalid = validated.firstNames !== false && validated.lastNames !== false;

    const showRightSideContent = !!searchResults || (!!patientssn && !patientid);
    return this.props.extended ? (
      <ExtendedSearch
        state={extendedSearchState}
        onChangePlatform={this.onChangePlatform}
        onChange={this.onChangeExtendedSearchField}
        onChangeLogOp={this.onChangeLogicalOperator}
        extendedSearch={this.extendedSearch}
        showSearchResults={showRightSideContent}
        onClickResult={(...args) => {
          ws_manager.disconnect();
          this.moveToVisitFromSearch(...args);
        }}
        searchStringArray={makeFinalSearchStringArray(
          this.state.platform,
          this.state.searchFields,
          this.state.logicalOperators,
        )}
        moveToGroupMessages={this.props.moveToGroupMessages}
      />
    ) : (
      <Container>
        <Item
          xs={6}
          style={{
            borderRight: `1px solid ${showRightSideContent ? colors.gray : 'transparent'}`,
          }}
          onKeyUp={this.handleEnter}
        >
          <StyledSearchArea sx={{ paddingRight: { xs: '4rem', md: '20rem' } }}>
            <StyledHeader>
              <FormattedMessage id="general.searchTitle" />
            </StyledHeader>
            <div style={{ marginBottom: '1.5rem' }}>
              <StyledSearchInfo>
                <FormattedMessage id="general.searchInfo" />
              </StyledSearchInfo>
            </div>
            <div style={{ marginBottom: '2.5rem' }}>
              <InputHandler
                type="TextField"
                editing={true}
                fullWidth={true}
                name="patientSearch"
                placeholder="general.searchPlaceholder"
                formData={{
                  document: { patientSearch: searchText },
                  onChange: this.onChangeSearchText,
                }}
                disableDelay
              />
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <ActionButtonRounded
                width={6}
                height={4}
                fontSize={16}
                text="general.search"
                onClick={this.search}
                disabled={!(this.state.searchText.trim().length > 0) || validated?.searchText === false}
                disabledTooltip={<FormattedMessage id="general.searchDisabledInfo" />}
                filled
              />
            </div>
          </StyledSearchArea>
        </Item>
        <Item xs={6} style={{ paddingLeft: '4rem' }}>
          {showRightSideContent &&
            (this.state.searchLoading ? (
              <SearchSpinner text="general.searching" />
            ) : (
              <React.Fragment>
                {searchResults && searchResults.length > 0 ? (
                  <NewSearchResults
                    searchResults={searchResults}
                    onClickResult={(...args) => {
                      ws_manager.disconnect();
                      this.moveToVisitFromSearch(...args);
                    }}
                    emptySearchResults={
                      (this.props.settings.orgSettings.allowedPatientCountries ?? []).length > 1
                        ? this.props.emptySearchResults
                        : undefined
                    }
                  />
                ) : (
                  <React.Fragment>
                    <div style={{ marginBottom: '2.5rem' }}>
                      <CreationForm
                        form={newForm}
                        onChangeNewForm={this.onChangeNewForm}
                        validated={validated}
                        patient={
                          patientssn
                            ? {
                                ssn: patientssn,
                                lastNames: patientlastnames,
                                firstNames: patientfirstnames,
                              }
                            : undefined
                        }
                        ssnValid={this.state.ssnValid}
                        ssnInvalidMessage={this.state.ssnInvalidMessage}
                        orgSettings={this.props.settings.orgSettings}
                      />
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                      <ActionButtonRounded
                        width={14}
                        height={4}
                        fontSize={16}
                        text="general.createPatient"
                        onClick={() => {
                          ws_manager.disconnect();
                          this.submitNewPatient();
                        }}
                        disabled={
                          !this.state.ssnValid || !namesPresent || !namesNotInvalid || this.state.ssnBRValidationLoading
                        }
                        loading={this.state.ssnBRValidationLoading}
                        disabledTooltip={
                          !namesPresent ? <FormattedMessage id="general.createPatientDisabledInfo" /> : undefined
                        }
                        filled
                      />
                    </div>
                  </React.Fragment>
                )}
              </React.Fragment>
            ))}
        </Item>
      </Container>
    );
  }
}

export interface IOwnState extends IExtendedSearch {
  searchLoading: boolean;
  searchText: string;
  wildcard: 'start' | 'end' | 'both';
  selected: string;
  newForm: IPatientDataForm;
  validated: { [key: string]: boolean | string | null };
  goToVisitReason: boolean;
  org: string;
  ssnValid: boolean;
  ssnInvalidMessage: string;
  ssnBRValidationLoading: boolean;
}

interface IDispatchFromProps {
  loadSession: () => void;
  clearAllPatientData: () => void;
  doPatientSearch: (searchString: string, wildcard: string) => Promise<true>;
  doPatientExtendedSearch: (searchString: string) => Promise<true>;
  emptySearchResults: () => void;
  clearSearchResults: () => void;
}

interface IStateFromProps {
  session: ISessionStore;
  searchResults: (TNeuroPersonData | IExtendedSearchResults)[] | null;
  settings: ISettingsStore;
}

const mapDispatchToProps = (dispatch: TDispatch): IDispatchFromProps => ({
  loadSession: (): void => dispatch(actions.loadSession()),
  clearAllPatientData: (): void => dispatch(clearAllPatientDataAndSettings()),
  doPatientSearch: (searchString: string, wildcard: string): Promise<true> =>
    dispatch(searchActions.doPatientSearch(searchString, wildcard)),
  doPatientExtendedSearch: (searchString: string): Promise<true> =>
    dispatch(searchActions.doExtendedSearch(searchString)),
  emptySearchResults: () => dispatch(searchActions.extendedSearchAction([])),
  clearSearchResults: (): void => dispatch(searchActions.clearSearchResults),
});

const mapStateToProps = (state: RootState): IStateFromProps => ({
  session: state.session || {},
  searchResults: state.patientsearch.searchResults,
  settings: state.settings,
});

interface IOwnProps {
  extended: boolean;
  moveToGroupMessages?: () => void;
}

export default connect(mapStateToProps, mapDispatchToProps)(PatientSearchAndCreation);
